import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import {
  DueDateType,
  ITaskTypeList,
  TaskStatusType,
  TaskType,
} from '../models/task-type';
import { BaseRestService } from './base-rest.service';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';

export enum enumEmailOrLetter {
  None = 1,
  Email = 2,
  Letter = 3,
}

@Injectable({ providedIn: 'root' })
export class TaskTypeService extends BaseRestService<TaskType, ITaskTypeList> {
  emailOrLetterTypes = [
    { id: 1, item: 'None' },
    { id: 2, item: 'Email' },
    { id: 3, item: 'Letter' },
  ];

  constructor(http: HttpClient, protected router: Router) {
    super(TaskTypeService, http, 'api/tasktypes', 'task type', router);

    // enable caching
    this.cacheEnabled = true;
  }

  hydrate(item: TaskType): Observable<TaskType> {
    return of(item);
  }

  public getTaskTypesList(): TaskType[] {
    return this.getCache().sort((a: TaskType, b: TaskType) => {
      return a.name > b.name ? 1 : -1;
    });
  }

  public getTaskTypeById(taskTypeId: number): TaskType {
    return this.getCache().find((task) => task.id === taskTypeId);
  }

  public getActiveTaskTypesList(): TaskType[] {
    return this.getTaskTypesList().filter((task) => task.hideRecord === false);
  }

  public getActiveTaskTypesWithoutAttachmentsList(): TaskType[] {
    return this.getTaskTypesList().filter(
      (task) =>
        task.hideRecord === false &&
        (!task.attachedDocXFiles || task.attachedDocXFiles === 0)
    );
  }
}

// tslint:disable-next-line:max-classes-per-file
@Injectable()
export class DueDateTypeService {
  dueDateTypesList: DueDateType[] = [
    { id: 1, name: 'Follow on days' },
    { id: 2, name: 'Follow up today' },
    { id: 3, name: 'Follow up date selection' },
    { id: 4, name: 'User selected follow up days' },
  ];

  public setDueDateTypes(newDueDateTypes: DueDateType[]) {
    this.dueDateTypesList = newDueDateTypes;
  }

  public getDueDateTypes() {
    return this.dueDateTypesList;
  }

  public getById(idValue: number): DueDateType {
    const lst: DueDateType[] = this.getDueDateTypes().filter(
      (x) => x.id === idValue
    );
    if (lst.length > 0) {
      return lst[0];
    } else {
      return null;
    }
  }

  public getDescription(idValue: number): string {
    const item: DueDateType = this.getById(idValue);
    if (item != null) {
      return item.name;
    } else {
      return idValue.toString();
    }
  }
}

// tslint:disable-next-line:max-classes-per-file
@Injectable()
export class TaskStatusService {
  taskStatusTypeList: TaskStatusType[] = [
    { id: 10, name: 'Open' },
    { id: 20, name: 'Late' },
    { id: 30, name: 'Completed' },
    { id: 40, name: 'Abandoned' },
  ];

  public setTaskStatusTypes(newTaskStatusList: TaskStatusType[]) {
    this.taskStatusTypeList = newTaskStatusList;
  }

  public getTaskStatusTypes(): TaskStatusType[] {
    return this.taskStatusTypeList;
  }

  public getTaskStatusTypeById(taskStatus: number): TaskStatusType {
    return this.taskStatusTypeList.find(
      (taskStatusType: TaskStatusType) => taskStatusType.id === taskStatus
    );
  }

  public getTaskStatusNameById(taskStatus: number): string {
    const ts = this.getTaskStatusTypeById(taskStatus);
    if (ts) {
      return ts.name;
    }
    return 'Unknown Task Status ' + taskStatus;
  }

  public getById(idValue: number): Observable<TaskStatusType> {
    const lst: TaskStatusType[] = this.getTaskStatusTypes().filter(
      (x) => x.id === idValue
    );
    return lst.length > 0 ? of(lst[0]) : null;
  }

  public getDescription(idValue: number): any {
    this.getById(idValue).pipe(
      take(1),
      map((i) => {
        return i.name;
      })
    );
  }
}
